import { effectScope, shallowRef, watch, type Ref } from 'vue';
import type { HydratedDocument } from '@pleased-npm/fe-customisation';
import { useCustomisationStore } from '../stores/customisation.store';
import {
  applyThemeToDocument,
  buildDocumentFromRegistry,
  buildDocumentFromTemplate,
  DocumentType,
  DocumentStatus,
  PUI,
} from '@pleased-npm/fe-customisation';
import { storeToRefs } from 'pinia';
import { logger } from '@/common/api/logger';
import { getRegistryMap, getHydratedThemeForRegistry, getThemeRegistry } from '../utils/registryLoader';
import { composableTracker } from '../utils/composableTracker';

export interface GetPageParams {
  registryId: string;
}

const registries = getRegistryMap();

/** One ref + one long-lived effect per registryId; shared by all callers so store/effect run once per id. */
const pageCache = new Map<string, { ref: Ref<HydratedDocument | null>; scope: ReturnType<typeof effectScope> }>();

/**
 * Clear all cached page refs and their associated effect scopes.
 * Only intended for tests — allows each test to start from a clean state.
 */
export const resetPageCache = (): void => {
  for (const { scope } of Array.from(pageCache.values())) {
    scope.stop();
  }
  pageCache.clear();
};

/**
 * Get a page document by name and apply theme to it.
 * If document is missing, builds it from registry defaults.
 * If theme is missing, builds it from theme registry defaults.
 *
 * Returns a reactive ref that updates when documents or theme change.
 * Result is cached per registryId (shared ref + effectScope) so duplicate callers don't repeat work.
 *
 * @param params - Object containing registryId
 * @returns Ref of HydratedDocument with theme values applied to ref fields, or null if not found
 */
export const useGetPage = ({ registryId }: GetPageParams): Ref<HydratedDocument | null> => {
  const entry = pageCache.get(registryId);
  if (entry) {
    composableTracker.track('useGetPage', 'cacheHit', registryId);
    return entry.ref;
  }

  composableTracker.track('useGetPage', 'cacheMiss', registryId);

  const store = useCustomisationStore();
  const { getDocumentByRegistryId } = store;
  const { activeTheme, documents } = storeToRefs(store);

  const page = shallowRef<HydratedDocument | null>(null);
  const scope = effectScope(true);
  scope.run(() => {
    // Use `watch` instead of `watchEffect` to avoid Vue 2.7's `instance._isDestroyed` bail-out.
    // `watchEffect` captures `currentInstance` at call time. If that component is later destroyed
    // (e.g. via v-if toggle), its getter returns early, empties deps, and the watcher goes dead.
    // `watch` with explicit sources does not have this check, so the watcher survives across
    // component lifecycle changes while still living in the detached effectScope(true).
    watch(
      [documents, activeTheme],
      () => {
        composableTracker.track('useGetPage', 'watchFired', registryId);

        const registry = registries.get(registryId);
        const pageDoc = getDocumentByRegistryId(registryId);

        if (registryId === PUI.REGISTRY_IDS.DEFAULT_THEME) {
          const hydratedTheme = getHydratedThemeForRegistry(activeTheme.value);
          const themeReg = getThemeRegistry();

          const document = !pageDoc
            ? buildDocumentFromRegistry({
                registry: themeReg,
                theme: hydratedTheme,
                params: {
                  type: DocumentType.PAGE,
                  name: themeReg.name,
                  active: true,
                  isPublished: DocumentStatus.SAVED,
                },
              })
            : buildDocumentFromTemplate({
                registry: themeReg,
                theme: hydratedTheme,
                template: pageDoc,
              });

          page.value = applyThemeToDocument({
            document,
            theme: hydratedTheme,
          });

          return;
        }

        if (!registry) {
          logger.error(`[USE_GET_PAGE] Registry not found for registryId ${registryId}`, { registryId });
          page.value = null;
          return;
        }

        const hydratedTheme = getHydratedThemeForRegistry(activeTheme.value);

        // When a stored document exists, merge it over fresh registry defaults instead of
        // using it as-is. This migrates documents saved against an older registry version,
        // so fields added later (e.g. the per-input `required` toggle) fall back to their
        // registry default rather than being missing and crashing downstream getters.
        const document = !pageDoc
          ? buildDocumentFromRegistry({
              registry,
              theme: hydratedTheme,
              params: {
                type: DocumentType.PAGE,
                name: registry.name,
                active: true,
                isPublished: DocumentStatus.SAVED,
              },
            })
          : buildDocumentFromTemplate({
              registry,
              theme: hydratedTheme,
              template: pageDoc,
            });

        const themedDocument = applyThemeToDocument({
          document,
          theme: hydratedTheme,
        });

        page.value = themedDocument;
      },
      { immediate: true }
    );
  });

  pageCache.set(registryId, { ref: page, scope });
  return page;
};
